import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X, Mail, MessageCircle, Globe, ArrowUpRight } from 'lucide-react';
import { PillotLogo } from '@/components/ModuleIcon';
import { LanguageToggle } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/lib/i18n';
import { useLanding, socialIcon, waLink } from '@/lib/landing';
import { useCopy } from '@/lib/landingCopy';

export const PublicNav = () => {
  const { user } = useAuth();
  const { lang } = useLang();
  const c = useCopy(lang);
  const [open, setOpen] = useState(false);
  const NAV = [{ to: '/#fitur', label: c.nav_features, hash: true, key: 'fitur' }, { to: '/pricing', label: c.nav_pricing, key: 'harga' }, { to: '/library', label: c.nav_library, key: 'library' }];
  const linkCls = ({ isActive }) => `text-sm font-medium transition-colors hover:text-primary ${isActive ? 'text-primary' : 'text-foreground/70'}`;
  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-[hsl(var(--pillot-ivory))]/85 backdrop-blur-xl" data-testid="public-nav">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" data-testid="public-nav-logo"><PillotLogo className="h-8" /></Link>
        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => n.hash ? <a key={n.key} href={n.to} className="text-sm font-medium text-foreground/70 hover:text-primary transition-colors" data-testid={`public-nav-${n.key}`}>{n.label}</a> : <NavLink key={n.key} to={n.to} className={linkCls} data-testid={`public-nav-${n.key}`}>{n.label}</NavLink>)}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <LanguageToggle />
          {user ? <Button asChild className="rounded-full h-10 px-5" data-testid="public-nav-dashboard"><Link to="/apps">{c.nav_dashboard} <ArrowUpRight size={16} className="ml-1" /></Link></Button> : (
            <>
              <Button asChild variant="ghost" className="rounded-full h-10 px-5" data-testid="public-nav-login"><Link to="/login">{c.nav_login}</Link></Button>
              <Button asChild className="rounded-full h-10 px-5" data-testid="public-nav-register"><Link to="/register">{c.nav_register}</Link></Button>
            </>
          )}
        </div>
        <div className="md:hidden flex items-center gap-2"><LanguageToggle /><button type="button" className="p-2" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="public-nav-menu-toggle">{open ? <X size={22} /> : <Menu size={22} />}</button></div>
      </div>
      {open && (
        <div className="md:hidden border-t border-border bg-white px-4 py-4 space-y-3" data-testid="public-nav-mobile">
          {NAV.map((n) => <a key={n.key} href={n.to} onClick={() => setOpen(false)} className="block text-sm font-medium py-1">{n.label}</a>)}
          <div className="flex gap-2 pt-2">
            {user ? <Button asChild className="rounded-full flex-1"><Link to="/apps">{c.nav_dashboard}</Link></Button> : <><Button asChild variant="outline" className="rounded-full flex-1"><Link to="/login">{c.nav_login}</Link></Button><Button asChild className="rounded-full flex-1"><Link to="/register">{c.nav_register}</Link></Button></>}
          </div>
        </div>
      )}
    </header>
  );
};

export const PublicFooter = () => {
  const d = useLanding();
  const { lang } = useLang();
  const c = useCopy(lang);
  const socials = d?.social_links || [];
  return (
    <footer className="bg-[hsl(var(--pillot-navy-2))] text-white" data-testid="public-footer">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14 grid gap-10 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <div>
          <div className="inline-flex rounded-xl bg-white px-3 py-2"><img src="/pillot-logo.png" alt="Pillot" className="h-7 w-auto" /></div>
          <p className="mt-4 text-sm text-white/65 max-w-sm leading-relaxed">{c.footer_desc}</p>
          {socials.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2" data-testid="footer-socials">
              {socials.map((s, i) => { const Icon = socialIcon(s.platform); return <a key={i} href={s.url} target="_blank" rel="noreferrer" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/80 hover:bg-white hover:text-[hsl(var(--pillot-navy-2))] transition-colors" aria-label={s.platform} data-testid={`footer-social-${s.platform}`}><Icon size={16} /></a>; })}
            </div>
          )}
        </div>
        <div>
          <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-white/50">{c.footer_product}</h4>
          <ul className="mt-4 space-y-2.5 text-sm">
            <li><a href="/#fitur" className="hover:text-[hsl(var(--pillot-mint))]">{c.nav_features}</a></li>
            <li><Link to="/pricing" className="hover:text-[hsl(var(--pillot-mint))]">{c.nav_pricing}</Link></li>
            <li><Link to="/library" className="hover:text-[hsl(var(--pillot-mint))]">{c.footer_library}</Link></li>
            <li><Link to="/register" className="hover:text-[hsl(var(--pillot-mint))]">{c.nav_register}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-white/50">{c.footer_legal}</h4>
          <ul className="mt-4 space-y-2.5 text-sm">
            <li><Link to="/terms" className="hover:text-[hsl(var(--pillot-mint))]" data-testid="footer-terms">{c.terms}</Link></li>
            <li><Link to="/privacy" className="hover:text-[hsl(var(--pillot-mint))]" data-testid="footer-privacy">{c.privacy}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-white/50">{c.footer_contact}</h4>
          <ul className="mt-4 space-y-2.5 text-sm" data-testid="footer-contact">
            {d?.contact_email && <li><a href={`mailto:${d.contact_email}`} className="flex items-center gap-2 hover:text-[hsl(var(--pillot-mint))]" data-testid="footer-contact-email"><Mail size={14} />{d.contact_email}</a></li>}
            {d?.contact_whatsapp && <li><a href={waLink(d.contact_whatsapp)} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[hsl(var(--pillot-mint))]" data-testid="footer-contact-wa"><MessageCircle size={14} />{d.contact_whatsapp}</a></li>}
            {d?.contact_website && <li><a href={`https://${d.contact_website.replace(/^https?:\/\//, '')}`} className="flex items-center gap-2 hover:text-[hsl(var(--pillot-mint))]" data-testid="footer-contact-web"><Globe size={14} />{d.contact_website}</a></li>}
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10"><div className="mx-auto max-w-7xl px-4 sm:px-6 py-5 text-xs text-white/45 flex flex-col sm:flex-row justify-between gap-2"><span>© {new Date().getFullYear()} Pillot · pillot.id</span><span>{c.footer_made}</span></div></div>
    </footer>
  );
};

export const PublicLayout = ({ children }) => (
  <div className="min-h-screen bg-[hsl(var(--pillot-ivory))] text-foreground flex flex-col">
    <PublicNav />
    <main className="flex-1">{children}</main>
    <PublicFooter />
  </div>
);

export const Section = ({ id, eyebrow, title, desc, children, className = '', testid }) => (
  <section id={id} className={`mx-auto max-w-7xl px-4 sm:px-6 py-20 ${className}`} data-testid={testid}>
    {(eyebrow || title) && (
      <div className="max-w-2xl mb-12">
        {eyebrow && <span className="inline-block rounded-full bg-[hsl(var(--pillot-mint-soft))] px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--pillot-mint))]">{eyebrow}</span>}
        {title && <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">{title}</h2>}
        {desc && <p className="mt-4 text-base text-muted-foreground leading-relaxed">{desc}</p>}
      </div>
    )}
    {children}
  </section>
);
